import { t } from '@lingui/macro';
import {
  type ApiClient,
  FormationCreatedFrom,
  QuarkFormationLevels,
  QuarkFormations,
  RefDesignations,
} from '@prismo-io/schemas';
import { z } from 'zod';

const DesignationSchema = z.object({
  code: RefDesignations.shape.code,
  referential_id: RefDesignations.shape.referential_id,
});

const ParamsSchema = z.object({
  id: z.string().uuid().nullable(),
  name: z.string(),
  level_id: QuarkFormationLevels.shape.id.nullish(),
  establishment: z.string().nullable(),
  favorite: z.number().int().nullable(),
  yearStart: z.string().nullable(),
  yearEnd: z.string().nullable(),
  designations: z.array(DesignationSchema),
  createdFrom: FormationCreatedFrom.optional(),
});

export type ParamsSchemaT = z.infer<typeof ParamsSchema>;

const ResultSchema = QuarkFormations.pick({ id: true });

export type ResultSchemaT = z.infer<typeof ResultSchema>;

export const sendFormation = async (
  client: ApiClient,
  params: ParamsSchemaT
): Promise<ResultSchemaT> => {
  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session) {
    throw new Error(t`Vous devez être connecté.`);
  }

  const userId = session.user.id;

  const validParams = ParamsSchema.parse(params);

  if (validParams.favorite) {
    const { data: formation } = await client
      .from('quark_formations')
      .select('id')
      .eq('favorite', validParams.favorite)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle()
      .throwOnError();

    if (formation) {
      await client
        .from('quark_formations')
        .update({
          favorite: null,
        })
        .eq('id', formation.id)
        .throwOnError();
    }
  }

  const { data: formation } = await client
    .from('quark_formations')
    .upsert({
      id: validParams.id ?? undefined,
      name: validParams.name,
      level_id: validParams.level_id ?? undefined,
      establishment: validParams.establishment,
      favorite: validParams.favorite,
      start_at: validParams.yearStart,
      end_at: validParams.yearEnd,
      created_from: validParams.createdFrom,
    })
    .select('id')
    .single()
    .throwOnError();

  if (!formation) {
    throw new Error('Erreur lors de la création.');
  }

  await client
    .from('quark_formations_designations')
    .delete()
    .eq('formation_id', formation.id)
    .throwOnError();

  const { designations } = validParams;

  try {
    await client
      .from('quark_formations_designations')
      .insert(
        designations.map((des) => {
          return {
            formation_id: formation.id,
            designation_code: des.code,
            designation_referential_id: des.referential_id,
          };
        })
      )
      .throwOnError();
  } catch (error) {
    await client.from('quark_formations').delete().eq('id', formation.id);

    throw error;
  }

  return ResultSchema.parse(formation);
};
